import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

/**
 * A button that opens a list of actions.
 *
 * It exists because a toolbar of six equal buttons says nothing about which one
 * you want. Grouping them under one trigger puts the choice inside the menu,
 * where the items can be labelled and sectioned, instead of across a row where
 * they can only be read left to right.
 *
 * Keyboard behaviour is the part worth being careful about, since this replaces
 * buttons that were reachable by Tab alone: arrows move, Home/End jump, Escape
 * closes and returns focus to the trigger, and typing a letter jumps to the next
 * item starting with it. Menu items are `<button>`s in a `<div role="menu">`,
 * which is what screen readers expect here.
 */

export interface MenuSection {
  /** Optional heading. These are the "subcategories" a long menu needs. */
  label?: string;
  items: MenuItem[];
}

export interface MenuItem {
  label: string;
  /** Second line, for when the label alone does not say what will happen. */
  hint?: string;
  icon?: ReactNode;
  onSelect: () => void;
  disabled?: boolean;
}

export function Menu({
  trigger,
  sections,
  align = 'end',
  variant = 'primary',
  className = '',
}: {
  trigger: ReactNode;
  sections: MenuSection[];
  align?: 'start' | 'end';
  variant?: 'primary' | 'secondary';
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const menuId = useId();

  // Flattened, because keyboard movement runs across sections but the headings
  // are not stops on the way.
  const flat = sections.flatMap((s) => s.items);
  const enabledIndexes = flat.map((it, i) => (it.disabled ? -1 : i)).filter((i) => i >= 0);

  const close = (restoreFocus = true) => {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  };

  useEffect(() => {
    if (!open) return;
    itemRefs.current[active]?.focus();
  }, [open, active]);

  useEffect(() => {
    if (!open) return;
    // Pointer down rather than click: a click that lands outside should dismiss
    // the menu without also activating whatever it landed on.
    const onPointerDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open]);

  const step = (delta: number) => {
    const pos = enabledIndexes.indexOf(active);
    const next = enabledIndexes[(pos + delta + enabledIndexes.length) % enabledIndexes.length];
    if (next !== undefined) setActive(next);
  };

  const openAt = (where: 'first' | 'last') => {
    const target =
      where === 'first' ? enabledIndexes[0] : enabledIndexes[enabledIndexes.length - 1];
    setActive(target ?? 0);
    setOpen(true);
  };

  const onMenuKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        step(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        step(-1);
        break;
      case 'Home':
        e.preventDefault();
        setActive(enabledIndexes[0] ?? 0);
        break;
      case 'End':
        e.preventDefault();
        setActive(enabledIndexes[enabledIndexes.length - 1] ?? 0);
        break;
      case 'Escape':
        e.preventDefault();
        close();
        break;
      case 'Tab':
        // Tab out of a menu closes it, but the focus move is the browser's.
        setOpen(false);
        break;
      default:
        if (e.key.length === 1 && /\S/.test(e.key)) {
          const from = enabledIndexes.indexOf(active);
          const order = [...enabledIndexes.slice(from + 1), ...enabledIndexes.slice(0, from + 1)];
          const hit = order.find((i) =>
            flat[i]?.label.toLowerCase().startsWith(e.key.toLowerCase()),
          );
          if (hit !== undefined) {
            e.preventDefault();
            setActive(hit);
          }
        }
    }
  };

  const chrome =
    variant === 'primary'
      ? 'bg-leaf text-cream hover:bg-leaf-soft'
      : 'border border-line bg-paper text-ink hover:bg-cream';

  let index = -1;

  return (
    <div className="relative" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => (open ? close(false) : openAt('first'))}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            openAt('first');
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            openAt('last');
          }
        }}
        className={`inline-flex min-h-tap items-center justify-center gap-2 rounded px-4 text-body font-medium transition-colors ${chrome} ${className}`}
      >
        {trigger}
        <svg
          aria-hidden="true"
          viewBox="0 0 10 6"
          className={`h-1.5 w-2.5 transition-transform ${open ? 'rotate-180' : ''}`}
        >
          <path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      </button>

      {open && (
        <div
          aria-hidden="true"
          onPointerDown={() => setOpen(false)}
          className="fixed inset-0 z-40 bg-ink/20 sm:hidden"
        />
      )}

      {open && (
        <div
          id={menuId}
          role="menu"
          aria-orientation="vertical"
          onKeyDown={onMenuKeyDown}
          /*
           * A sheet on a phone, an anchored dropdown from `sm` up.
           *
           * **`fixed`, not `absolute`, and that is the bug fix.** `html, body`
           * carry `overflow-x: hidden`, and CSS turns the other axis to `auto`
           * when one is hidden — so the body is a scroll container and clipped
           * every absolutely positioned menu inside it. The menu was opening and
           * being cut off, which reads as the button doing nothing. A fixed
           * element is not clipped by a scrolling ancestor, so this escapes it.
           *
           * It is also the better shape for a thumb: full width at the bottom of
           * the screen, where the hand already is, rather than a small panel at
           * the top where the trigger happens to be. `AskLauncher` is the same
           * pattern, so the app has one way of showing a surface over another.
           */
          className={`panel fixed inset-x-0 bottom-0 z-50 max-h-[70vh] overflow-y-auto rounded-t py-1 pb-[max(0.5rem,env(safe-area-inset-bottom))] ring-1 ring-line sm:absolute sm:inset-x-auto sm:bottom-auto sm:z-30 sm:mt-2 sm:max-h-none sm:w-[19rem] sm:max-w-[calc(100vw-2rem)] sm:overflow-hidden sm:rounded sm:pb-1 ${
            align === 'end' ? 'sm:right-0' : 'sm:left-0'
          }`}
        >
          {sections.map((section, si) => (
            <div key={section.label ?? si} role="group" aria-label={section.label}>
              {si > 0 && <div role="separator" className="my-1 h-px bg-line" />}
              {section.label && (
                <p className="eyebrow px-3 pb-1 pt-2 text-leaf-soft">{section.label}</p>
              )}
              {section.items.map((item) => {
                index += 1;
                const i = index;
                return (
                  <button
                    key={item.label}
                    ref={(el) => {
                      itemRefs.current[i] = el;
                    }}
                    type="button"
                    role="menuitem"
                    tabIndex={i === active ? 0 : -1}
                    disabled={item.disabled}
                    onMouseEnter={() => {
                      if (!item.disabled) setActive(i);
                    }}
                    onClick={() => {
                      close(false);
                      item.onSelect();
                    }}
                    className="flex w-full items-start gap-3 px-3 py-2 text-left transition-colors hover:bg-cream focus:bg-cream focus:outline-none disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {item.icon && (
                      <span aria-hidden="true" className="mt-0.5 text-leaf-soft">
                        {item.icon}
                      </span>
                    )}
                    <span className="flex flex-col">
                      <span className="text-body text-ink">{item.label}</span>
                      {item.hint && (
                        <span className="text-caption text-leaf-soft">{item.hint}</span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
